<template>
	<div class="row mt-4 justify-content-center">
		<div class="col-md-12">
		  <h1 class="fs-2 mb-0">Visit History Report</h1>
		  <div class="card border-0 shadow-sm">
		    <div class="card-header bg-white pt-3 pb-3 border-bottom">
		      <div class="row align-items-center g-3">
		        <div class="col-md-4">
		          <span class="text-muted">
		            Showing {{ activeWindows.length }} of {{ visitWindows.length }} visit windows
		            ({{ processedParticipants.length }} {{ processedParticipants.length === 1 ? 'participant' : 'participants' }})
		          </span>
		        </div>

		        <div class="col-md-5 d-flex align-items-center gap-2">
		          <label class="form-label mb-0 small fw-bold text-nowrap text-muted">View Windows:</label>
		          <div class="btn-group btn-group-sm" role="group">
		            <button 
		              type="button" 
		              class="btn btn-outline-secondary"
		              :class="{ 'active': windowPreset === 'focused' }"
		              @click="windowPreset = 'focused'"
		            >
		              Recent & Upcoming
		            </button>
		            <button 
		              type="button" 
		              class="btn btn-outline-secondary"
		              :class="{ 'active': windowPreset === 'all' }"
		              @click="windowPreset = 'all'"
		            >
		              Show All ({{ visitWindows.length }})
		            </button>
		          </div>
		        </div>

		        <!-- Search Filter Input -->
		        <div class="col-md-3">
		          <div class="input-group input-group-sm">
		            <span class="input-group-text bg-light"><i class="bi bi-search"></i></span>
		            <input
		              v-model="filter"
		              type="text"
		              class="form-control"
		              placeholder="Filter by name or STAR ID..."
		            />
		            <button 
		              v-if="filter" 
		              class="btn btn-outline-secondary" 
		              type="button" 
		              @click="filter = ''"
		            >
		              Clear
		            </button>
		          </div>
		        </div>
		      </div>
		    </div>

		    <!-- Table Container -->
		    <div class="card-body p-0">
		      <!-- Loading State -->
		      <div v-if="loading" class="text-center py-5">
		        <div class="spinner-border text-primary mb-2" role="status"></div>
		        <p class="text-muted mb-0">Loading visit history data&hellip;</p>
		      </div>

		      <!-- Main Table -->
		      <div 
		        v-else-if="processedParticipants.length > 0" 
		        class="table-responsive" 
		        style="max-height: 650px;"
		      >
		        <table class="table table-hover align-middle mb-0" id="visit-history-report-table">
		          <!-- Width & Grouping Column Specifications -->
		          <colgroup>
		            <col style="width: 120px;" />
		            <col style="width: 200px;" />
		          </colgroup>
		          <colgroup 
		            v-for="window in activeWindows" 
		            :key="'col_group_' + window.id"
		            class="border-start"
		          >
		            <col style="min-width: 130px;" />
		            <col style="min-width: 130px;" />
		          </colgroup>

		          <thead class="table-light sticky-top">
		            <!-- Top Row: Grouping Headers -->
		            <tr>
		              <th scope="col" colspan="2" class="border-bottom-0">Participant Details</th>
		              <th 
		                v-for="window in activeWindows" 
		                :key="'win_head_' + window.id"
		                scope="col"
		                colspan="2" 
		                class="text-center border-start border-bottom-0 fw-bold"
		                :class="{ 'bg-primary-subtle text-primary': window.id === currentWindowId }"
		              >
		                {{ window.name }}
		                <span v-if="window.id === currentWindowId" class="badge bg-primary ms-1">Current</span>
		              </th>
		            </tr>

		            <tr>
		              <th scope="col" class="user-select-none style-pointer" @click="setSortBy('star_id')">
		                STAR ID
		                <i class="bi ms-1" :class="getSortIcon('star_id')"></i>
		              </th>
		              <th scope="col" class="user-select-none style-pointer" @click="setSortBy('full_name')">
		                Name
		                <i class="bi ms-1" :class="getSortIcon('full_name')"></i>
		              </th>

		              <template v-for="window in activeWindows" :key="'win_sub_' + window.id">
										<th 
										  scope="col" 
										  class="user-select-none style-pointer border-start"
										  @click="setSortBy(`window_${window.id}_core`)"
										  style="background-color: #d1e7dd; color: #0f5132;"
										>
										  <span>Core</span>
										  <i class="bi ms-1" :class="getSortIcon(`window_${window.id}_core`)"></i>
										</th>

										<th 
										  scope="col" 
										  class="user-select-none style-pointer"
										  @click="setSortBy(`window_${window.id}_short`)"
										  style="background-color: #cff4fc; color: #055160;"
										>
										  <span>Short</span>
										  <i class="bi ms-1" :class="getSortIcon(`window_${window.id}_short`)"></i>
										</th>
		              </template>
		            </tr>
		          </thead>

		          <tbody>
		            <tr v-for="part in processedParticipants" :key="'participant_' + part.id">
		              <td class="fw-bold">{{ part.star_id }}</td>
		              <td>
		                <a :href="`/participants/${part.id}`">
		                  {{ part.first_name }} {{ part.last_name }}
		                </a>
		              </td>

		              <template v-for="window in activeWindows" :key="'cell_group_' + window.id">
									  <td class="border-start text-nowrap">
									    <div v-if="getCellData(part, window.id, 'core')" class="lh-sm">
									      <div>
									        <span 
									          :class="getStatusClass(getCellData(part, window.id, 'core').status)"
									        >
									          <i :class="getStatusIcon(getCellData(part, window.id, 'core').status)"></i>
									          {{ getCellData(part, window.id, 'core').status }}
									        </span>
									      </div>

									      <div v-if="getCellData(part, window.id, 'core').date" class="small mt-1">
									        <i class="bi bi-calendar-event me-1"></i>
									        {{ getCellData(part, window.id, 'core').date }}
									      </div>
									    </div>
									    <span v-else class="text-muted small">&mdash;</span>
									  </td>

									  <td class="text-nowrap">
									    <div v-if="getCellData(part, window.id, 'short')" class="lh-sm">
									      <div>
									        <span 
									          :class="getStatusClass(getCellData(part, window.id, 'short').status)"
									        >
									          <i :class="getStatusIcon(getCellData(part, window.id, 'short').status)"></i>
									          {{ getCellData(part, window.id, 'short').status }}
									        </span>
									      </div>

									      <div v-if="getCellData(part, window.id, 'short').date" class="small mt-1">
									        <i class="bi bi-calendar-event me-1"></i>
									        {{ getCellData(part, window.id, 'short').date }}
									      </div>
									    </div>
									    <span v-else class="text-muted small">&mdash;</span>
									  </td>
									</template>
		            </tr>
		          </tbody>
		        </table>
		      </div>

		      <div v-else class="text-center py-5 text-muted">
		        <i class="bi bi-inbox fs-1 d-block mb-2"></i>
		        <p class="mb-0 fs-6">No participants matched your search criteria.</p>
		      </div>
		    </div>
		  </div>
		</div>
	</div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import axios from 'axios';

const props = defineProps({
  visitWindows: {
    type: Array,
    default: () => []
  },
  currentWindowId: {
    type: [Number, String],
    default: null
  }
});

const filter = ref('');
const participants = ref([]);
const loading = ref(true);
const sortBy = ref('star_id');
const sortDir = ref('asc');
const windowPreset = ref('focused'); // 'focused' or 'all'

const activeWindows = computed(() => {
  if (windowPreset.value === 'all' || !props.currentWindowId) {
    return props.visitWindows;
  }

  const currentIndex = props.visitWindows.findIndex(w => w.id === props.currentWindowId);
  if (currentIndex === -1) return props.visitWindows;

  // Slices 2 past windows, current window, and 1 next window
  const startIndex = Math.max(0, currentIndex - 2);
  const endIndex = Math.min(props.visitWindows.length, currentIndex + 2);

  return props.visitWindows.slice(startIndex, endIndex);
});

const formatDate = (dateStr) => {
  if (!dateStr) return null;

  const [year, month, day] = dateStr.split('T')[0].split('-').map(Number);
  const localDate = new Date(year, month - 1, day);

  return localDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const getStatusClass = (status) => {
  switch (status) {
    case 'Attended':
      return 'badge bg-success'
    case 'Scheduled':
      return 'badge bg-primary'
    case 'Needs to be Scheduled':
  		return 'badge border border-warning text-warning-emphasis bg-warning-subtle'
    case 'Autocanceled':
      return 'text-muted fst-italic small'
    case 'Canceled':
      return 'badge bg-secondary'
    default:
      return 'fw-semibold small text-secondary'
  }
}

const getStatusIcon = (status) => {
  switch (status) {
    case 'Attended':
      return 'bi bi-check-circle-fill me-1'
    case 'Scheduled':
      return 'bi bi-calendar-check me-1'
    case 'Needs to be Scheduled':
    case 'Needs Scheduling':
      return 'bi bi-exclamation-triangle-fill me-1'
    case 'Auto-Canceled':
    case 'Autocanceled':
      return 'bi bi-slash-circle me-1'
    default:
      return ''
  }
}

const getCellData = (participant, windowId, type) => {
  const task = participant[`window_${windowId}`]?.[type];
  if (!task) return null;

  const status = task.task_type_status?.status_name ?? '';
  const appt = task.appointments?.[0];

  let date = null;
  if (status === 'Attended' || status === 'Completed') {
    date = appt?.starts_at;
  } else if (status === 'Scheduled') {
    date = appt?.starts_at;
  }

  return {
    status,
    date: formatDate(date)
  };
};

const setSortBy = (val) => {
  if (sortBy.value === val) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortBy.value = val;
    sortDir.value = 'asc';
  }
};

const getSortIcon = (columnKey) => {
  if (sortBy.value !== columnKey) {
    return 'bi-arrow-down-up text-secondary'; 
  }
  return sortDir.value === 'asc' ? 'bi-arrow-up text-primary fw-bold' : 'bi-arrow-down text-primary fw-bold';
};

const fetchParticipants = () => {
  loading.value = true;
  axios.get('/api/visit-history-report')
    .then((response) => {
      loading.value = false;
      participants.value = response.data ?? [];
    })
    .catch((e) => {
      loading.value = false;
    });
};

const processedParticipants = computed(() => {
  const query = filter.value.trim().toLowerCase();

  let list = participants.value;
  if (query) {
    list = list.filter((p) => {
      const fullName = `${p.first_name ?? ''} ${p.last_name ?? ''}`.toLowerCase();
      const id = String(p.id ?? '').toLowerCase();
      const starId = String(p.star_id ?? '').toLowerCase();
      return fullName.includes(query) || id.includes(query) || starId.includes(query);
    });
  }

  const direction = sortDir.value === 'asc' ? 1 : -1;
  return [...list].sort((a, b) => {
    let valA, valB;

    if (sortBy.value === 'star_id') {
      valA = a.star_id;
      valB = b.star_id;
    } else if (sortBy.value === 'full_name') {
      valA = `${a.first_name ?? ''} ${a.last_name ?? ''}`.toLowerCase();
      valB = `${b.first_name ?? ''} ${b.last_name ?? ''}`.toLowerCase();
    } else if (sortBy.value.startsWith('window_')) {
      const parts = sortBy.value.split('_');
      const windowId = parts[1];
      const type = parts[2];
      
      const cellA = getCellData(a, windowId, type);
      const cellB = getCellData(b, windowId, type);
      
      valA = cellA?.status ?? '';
      valB = cellB?.status ?? '';
    }

    if (valA == null) return 1 * direction;
    if (valB == null) return -1 * direction;

    if (valA < valB) return -1 * direction;
    if (valA > valB) return 1 * direction;
    return 0;
  });
});

onMounted(() => {
  fetchParticipants();
});
</script>

<style scoped>
.style-pointer {
  cursor: pointer;
}
.extra-small {
  font-size: 0.75rem;
}
</style>